import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  ArrowPathIcon, 
  XCircleIcon, 
  ExclamationTriangleIcon,
  InformationCircleIcon,
  DocumentIcon,
  CalendarIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  UserIcon,
  TagIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon as AlertIcon,
  PlusCircleIcon
} from '@heroicons/react/24/outline';
import toast from '../utils/toast';
import api from '../services/api';
import TaskCreateModal from './TaskCreateModal';

const TrademarkPortfolioDetail = () => {
  const { appno } = useParams();
  const navigate = useNavigate();
  const [trademarkData, setTrademarkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Task modal state
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);

  useEffect(() => {
    fetchTrademarkDetail();
  }, [appno]);

  const fetchTrademarkDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('📡 Fetching trademark detail for appno:', appno);
      const response = await api.post('/api-data/get-data', {
        url: `https://agent3.tmpilot.com/api/trademark/${appno}?token={token}`
      });

      if (response.data.success) {
        console.log('✅ Trademark detail received:', response.data.data);
        setTrademarkData(response.data.data.result);
        toast.success('Trademark details loaded successfully');
      } else {
        throw new Error(response.data.message || 'Failed to load trademark details');
      }
    } catch (error) {
      console.error('Error fetching trademark detail:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load trademark details';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'registered':
        return 'bg-green-100 text-green-800';
      case 'objected':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'abandoned':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const sortDocumentsByDate = (docNames, docUrls, docDates) => {
    if (!docNames || !docUrls || !docDates) return [];
    
    const documents = docNames.map((name, index) => ({
      name: name,
      url: docUrls[index] || '',
      date: docDates[index] || ''
    }));

    return documents.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA; // Sort by newest first
    });
  };

  const handleBackClick = () => {
    window.history.back();
    // navigate('/api-data/trademark-portfolio');
  };

  const handleCreateTask = () => {
    setIsTaskModalOpen(true);
  };

  const getTaskInitialData = () => {
    if (!trademarkData) return {};
    
    // Format description with trademark data
    const description = `App No: ${trademarkData.appno || 'N/A'}\nTM Applied For: ${trademarkData.tmAppliedFor || 'N/A'}\nBusiness Name: ${trademarkData.buisnessName || 'N/A'}`;
    
    return {
      name: `Task for ${trademarkData.appno || 'Trademark'}`,
      description: description
    };
  };

  const handleTaskCreated = () => {
    // Show success message
    toast.success('Task created successfully for trademark');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ArrowPathIcon className="h-12 w-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Loading trademark details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Details</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="space-x-4">
            <button
              onClick={fetchTrademarkDetail}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Try Again
            </button>
            <button
              onClick={handleBackClick}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!trademarkData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
          <p className="text-gray-600 mb-4">No trademark details found for this application number.</p>
          <button
            onClick={handleBackClick}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const sortedDocuments = sortDocumentsByDate(
    trademarkData.doc_name,
    trademarkData.doc_url,
    trademarkData.doc_date
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={handleBackClick}
                className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Back to Portfolio
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  Trademark Details
                </h1>
                <p className="text-sm text-gray-500">
                  Application Number: {trademarkData.appno}
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleCreateTask}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
              >
                <PlusCircleIcon className="h-5 w-5 mr-2" />
                Create Task
              </button>
            <button
              onClick={fetchTrademarkDetail}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <ArrowPathIcon className="h-5 w-5 mr-2" />
              Refresh
            </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(trademarkData.status)}`}>
                  {trademarkData.status}
                </span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Application Number</label>
                  <p className="text-sm text-gray-900 font-medium">{trademarkData.appno}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Class</label>
                  <p className="text-sm text-gray-900">{trademarkData.class}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Application</label>
                  <p className="text-sm text-gray-900">{formatDate(trademarkData.dateOfApp)}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valid Up To</label>
                  <p className="text-sm text-gray-900">{formatDate(trademarkData.validUpto)}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Certificate Number</label>
                  <p className="text-sm text-gray-900">{trademarkData.certNo || '-'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">User Detail</label>
                  <p className="text-sm text-gray-900">{trademarkData.userDetail || '-'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Hearing</label>
                  <p className="text-sm text-gray-900">{formatDate(trademarkData.DateofHearing)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <p className="text-sm text-gray-900">{trademarkData.type || '-'}</p>
                </div>
              </div>
            </div>

            {/* Trademark Information */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Trademark Information</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Trademark Applied For</label>
                  <div className="flex items-start space-x-4">
                    {trademarkData.imgurl && (
                      <div className="flex-shrink-0">
                        <img 
                          src={trademarkData.imgurl} 
                          alt="Trademark" 
                          className="w-24 h-24 object-contain border border-gray-200 rounded"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{trademarkData.tmAppliedFor}</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Goods and Services</label>
                  <p className="text-sm text-gray-900">{trademarkData.goodsAndSerice || '-'}</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Trademark Category</label>
                    <p className="text-sm text-gray-900">{trademarkData.tmcategory || '-'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Trademark Type</label>
                    <p className="text-sm text-gray-900">{trademarkData.tmtype || '-'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Information */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Business Information</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Proprietor Name</label>
                  <p className="text-sm text-gray-900">{trademarkData.propName || '-'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                  <p className="text-sm text-gray-900">{trademarkData.buisnessName || '-'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Type</label>
                  <p className="text-sm text-gray-900">{trademarkData.buisnessType || '-'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address of Applicant</label>
                  <p className="text-sm text-gray-900">{trademarkData.proprietorAdr || '-'}</p>
                </div>
              </div>
            </div>

            {/* Opposition Information */}
            {(trademarkData.oppnum && trademarkData.oppnum.length > 0) && (
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Opposition Information</h2>
                
                <div className="space-y-4">
                  {trademarkData.oppnum.map((oppNum, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Opposition Number</label>
                          <p className="text-sm text-gray-900">{oppNum || '-'}</p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Opposition Code</label>
                          <p className="text-sm text-gray-900">{trademarkData.oppcode?.[index] || '-'}</p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Opposition Date</label>
                          <p className="text-sm text-gray-900">{formatDate(trademarkData.oppdate?.[index])}</p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Opponent Name</label>
                          <p className="text-sm text-gray-900">{trademarkData.oppname?.[index] || '-'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* PR Details */}
            {trademarkData.pr_details && (
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">PR Details</h2>
                
                <div className="prose prose-sm max-w-none">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{trademarkData.pr_details}</p>
                </div>
              </div>
            )}

            {/* Documents */}
            {sortedDocuments.length > 0 && (
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Documents</h2>
                
                <div className="space-y-3">
                  {sortedDocuments.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <DocumentIcon className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                          {doc.date && (
                            <p className="text-xs text-gray-500">{formatDate(doc.date)}</p>
                          )}
                        </div>
                      </div>
                      {doc.url && (
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          View
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Changes */}
            {trademarkData.changes && trademarkData.changes.length > 0 && (
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Changes History</h2>
                
                <div className="space-y-4">
                  {trademarkData.changes.map((change, index) => (
                    <div key={change.id || index} className="border-l-4 border-blue-500 pl-4 py-3 bg-blue-50 rounded-r-lg">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="space-y-2">
                            {change.field && (
                              <p className="text-sm font-medium text-gray-900">
                                Field: <span className="font-normal capitalize">{change.field}</span>
                              </p>
                            )}
                            {change.old_value && (
                              <p className="text-sm text-gray-600">
                                <span className="font-medium">From:</span> {change.old_value || 'Empty'}
                              </p>
                            )}
                            {change.new_value && (
                              <p className="text-sm text-gray-600">
                                <span className="font-medium">To:</span> {change.new_value}
                              </p>
                            )}
                            {change.diff && (
                              <p className="text-xs text-gray-500">
                                <span className="font-medium">Time:</span> {change.diff}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="ml-4 flex-shrink-0">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Change {index + 1}
                          </span>
                        </div>
                      </div>
                      
                      {/* Change Details */}
                      {change.details && (
                        <div className="mt-3 pt-3 border-t border-blue-200">
                          <p className="text-xs font-medium text-gray-700 mb-2">Change Details:</p>
                          <div className="bg-white rounded p-2 text-xs text-gray-600">
                            {change.details.time && (
                              <p><span className="font-medium">Timestamp:</span> {change.details.time}</p>
                            )}
                            {change.details['0'] && (
                              <div className="mt-1">
                                <p><span className="font-medium">File:</span> {change.details['0'].file}</p>
                                <p><span className="font-medium">Line:</span> {change.details['0'].line}</p>
                                <p><span className="font-medium">Function:</span> {change.details['0'].function}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Logs */}
            {trademarkData.logs && trademarkData.logs.length > 0 && (
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Activity Logs</h2>
                
                <div className="space-y-3">
                  {trademarkData.logs.map((log, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg">
                      <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                      <div className="flex-1">
                        {typeof log === 'string' ? (
                          <p className="text-sm text-gray-900">{log}</p>
                        ) : (
                          <div>
                            {log.action && (
                              <p className="text-sm font-medium text-gray-900">{log.action}</p>
                            )}
                            {log.description && (
                              <p className="text-sm text-gray-600 mt-1">{log.description}</p>
                            )}
                            {log.timestamp && (
                              <p className="text-xs text-gray-500 mt-1">
                                {formatDate(log.timestamp)}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Location Information */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Location Information</h3>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <MapPinIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">State</p>
                    <p className="text-sm text-gray-600">{trademarkData.state || '-'}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <MapPinIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Country</p>
                    <p className="text-sm text-gray-600">{trademarkData.country || '-'}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Registry Location</p>
                    <p className="text-sm text-gray-600">{trademarkData.registryLocation || '-'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Agent Information */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Agent Information</h3>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <UserIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Agent Name</p>
                    <p className="text-sm text-gray-600">{trademarkData.agentName || '-'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Journal Information */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Journal Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Journal Number</label>
                  <p className="text-sm text-gray-900">{trademarkData.jnumber || '-'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Journal Date</label>
                  <p className="text-sm text-gray-900">{formatDate(trademarkData.JournalDate)}</p>
                </div>
              </div>
            </div>

            {/* Alerts */}
            {trademarkData.alerts && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Alerts</h3>
                
                <div className="flex items-start space-x-3">
                  <AlertIcon className="h-5 w-5 text-yellow-500 mt-0.5" />
                  <p className="text-sm text-gray-700">{trademarkData.alerts}</p>
                </div>
              </div>
            )}

            {/* PR Details */}
            {trademarkData.pr_details && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">PR Details</h3>
                
                <div className="text-sm text-gray-700">
                  <p>{trademarkData.pr_details}</p>
                </div>
              </div>
            )}

            {/* Opposition Information */}
            {(trademarkData.oppnum?.length > 0 || trademarkData.oppcode?.length > 0 || 
              trademarkData.oppdate?.length > 0 || trademarkData.oppname?.length > 0) && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Opposition Information</h3>
                
                <div className="space-y-3">
                  {trademarkData.oppnum?.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Opposition Numbers</label>
                      <div className="space-y-1">
                        {trademarkData.oppnum.map((opp, index) => (
                          <p key={index} className="text-sm text-gray-900">{opp}</p>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {trademarkData.oppcode?.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Opposition Codes</label>
                      <div className="space-y-1">
                        {trademarkData.oppcode.map((code, index) => (
                          <p key={index} className="text-sm text-gray-900">{code}</p>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {trademarkData.oppdate?.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Opposition Dates</label>
                      <div className="space-y-1">
                        {trademarkData.oppdate.map((date, index) => (
                          <p key={index} className="text-sm text-gray-900">{formatDate(date)}</p>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {trademarkData.oppname?.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Opponent Names</label>
                      <div className="space-y-1">
                        {trademarkData.oppname.map((name, index) => (
                          <p key={index} className="text-sm text-gray-900">{name}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Hearing Information */}
            {trademarkData.DateofHearing && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Hearing Information</h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Hearing</label>
                    <p className="text-sm text-gray-900">{formatDate(trademarkData.DateofHearing)}</p>
                  </div>
                  
                  {trademarkData.daysLeftToHearing && trademarkData.daysLeftToHearing !== '-' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Days Left to Hearing</label>
                      <p className="text-sm text-gray-900">{trademarkData.daysLeftToHearing}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Changes Summary */}
            {trademarkData.changes && trademarkData.changes.length > 0 && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Changes Summary</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Total Changes</span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {trademarkData.changes.length}
                    </span>
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    <p>This trademark has been updated {trademarkData.changes.length} time{trademarkData.changes.length !== 1 ? 's' : ''}.</p>
                    <p className="mt-1">View the full changes history in the main content area.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Activity Summary */}
            {trademarkData.logs && trademarkData.logs.length > 0 && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Summary</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Total Activities</span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {trademarkData.logs.length}
                    </span>
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    <p>This trademark has {trademarkData.logs.length} activity log{trademarkData.logs.length !== 1 ? 's' : ''}.</p>
                    <p className="mt-1">View the full activity history in the main content area.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Last Updated */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">System Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                  <p className="text-sm text-gray-900">{trademarkData.updatedate || '-'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <p className="text-sm text-gray-900">{trademarkData.type || '-'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Task Creation Modal */}
      <TaskCreateModal
        isOpen={isTaskModalOpen}
        onClose={() => {
          setIsTaskModalOpen(false);
        }}
        onSuccess={handleTaskCreated}
        initialData={getTaskInitialData()}
      />
    </div>
  );
};

export default TrademarkPortfolioDetail; 